import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ErrorState,
  IntegrationStatusCard,
  PageHeader,
  SectionHeader,
  SimpleTable,
  SourceBanner
} from '../../../shared/components/index.js';
import { useAnalyticsRefresh } from '../../../shared/hooks/useAnalyticsRefresh.js';
import StagesConfigPanel from '../components/StagesConfigPanel.jsx';
import OriginTagsPanel from '../components/OriginTagsPanel.jsx';
import {
  fetchConfigSnapshot,
  reloadCacheStatus,
  runEnsureCacheSheets,
  runInstallCacheTrigger,
  runRefreshSheetCache
} from '../services/configService.js';

const TABS = [
  { id: 'etapas', label: 'Etapas del embudo' },
  { id: 'origenes', label: 'Orígenes (etiquetas)' },
  { id: 'integraciones', label: 'Integraciones' },
  { id: 'fuentes', label: 'Fuentes de datos' },
  { id: 'usuarios', label: 'Usuarios' },
  { id: 'roles', label: 'Roles' },
  { id: 'sync', label: 'Sincronización' },
  { id: 'logs', label: 'Logs' },
  { id: 'sistema', label: 'Sistema' }
];

export default function ConfigPage() {
  const { email } = useOutletContext() || {};
  const [tab, setTab] = useState('etapas');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [cacheBusy, setCacheBusy] = useState('');
  const [cacheMsg, setCacheMsg] = useState('');
  const [cacheErr, setCacheErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await fetchConfigSnapshot();
      setData(snap);
    } catch (err) {
      setError((err && err.message) || 'Error al cargar configuración.');
    } finally {
      setLoading(false);
    }
  }, []);

  useAnalyticsRefresh(load);

  useEffect(() => {
    load();
  }, [load]);

  async function refreshCacheStatusOnly_() {
    try {
      const st = await reloadCacheStatus();
      setData((prev) =>
        prev
          ? {
              ...prev,
              cacheStatus: st,
              syncJobs: [
                {
                  name: 'Caché dashboards (planilla Analytics)',
                  lastRun: st.lastRefreshAt || '—',
                  nextRun: st.trigger && st.trigger.installed ? 'cada 30 min' : 'Trigger no instalado',
                  duration: st.lastRefreshMs ? st.lastRefreshMs + ' ms' : '—',
                  records: (st.lastRefreshOk || '0') + ' ok / ' + (st.lastRefreshErrors || '0') + ' err',
                  status: st.lastRefreshStatus || (st.openOk ? 'Listo' : 'Sin acceso'),
                  errors: st.openError || '—'
                }
              ]
            }
          : prev
      );
    } catch (e) {
      /* ignore — el mensaje de acción ya informa */
    }
  }

  async function onEnsureSheets_() {
    setCacheBusy('ensure');
    setCacheMsg('');
    setCacheErr('');
    try {
      const res = await runEnsureCacheSheets({ installTrigger: false });
      if (res && res.success === false) throw new Error(res.error || 'No se pudieron crear las hojas.');
      setCacheMsg(
        'Hojas listas en «' +
          ((res && res.spreadsheetName) || 'Analytics') +
          '»: ' +
          ((res && res.sheets && res.sheets.join(', ')) || 'ok')
      );
      await refreshCacheStatusOnly_();
    } catch (err) {
      setCacheErr((err && err.message) || String(err));
    } finally {
      setCacheBusy('');
    }
  }

  async function onInstallTrigger_() {
    setCacheBusy('trigger');
    setCacheMsg('');
    setCacheErr('');
    try {
      const res = await runInstallCacheTrigger();
      if (res && res.success === false) throw new Error(res.error || 'No se pudo instalar el trigger.');
      setCacheMsg('Trigger instalado: refresh automático cada 30 minutos.');
      await refreshCacheStatusOnly_();
    } catch (err) {
      setCacheErr((err && err.message) || String(err));
    } finally {
      setCacheBusy('');
    }
  }

  async function onRefreshCache_() {
    setCacheBusy('refresh');
    setCacheMsg('Actualizando caché (puede tardar varios minutos)…');
    setCacheErr('');
    try {
      const res = await runRefreshSheetCache({ installTrigger: true });
      if (res && res.success === false) throw new Error(res.error || 'Refresh falló.');
      const ok = (res.results || []).filter((r) => r.status === 'ok').length;
      const errN = (res.results || []).filter((r) => r.status === 'error').length;
      setCacheMsg(
        'Caché actualizado (' +
          (res.status || 'ok') +
          '): ' +
          ok +
          ' ok, ' +
          errN +
          ' errores · ' +
          (res.durationMs || 0) +
          ' ms' +
          (res.aborted ? ' · parcial (límite de tiempo; el trigger completará)' : '')
      );
      await refreshCacheStatusOnly_();
    } catch (err) {
      setCacheErr((err && err.message) || String(err));
      setCacheMsg('');
    } finally {
      setCacheBusy('');
    }
  }

  const cache = (data && data.cacheStatus) || null;

  return (
    <div className="analytics-module analytics-module-config">
      <PageHeader
        eyebrow="Módulo · Configuración"
        title="Configuración"
        subtitle={
          'Integraciones y operación del sistema. Los secretos no se muestran aquí.' +
          (email ? ` · ${email}` : '')
        }
      />

      <SourceBanner mode={data && !data.system?.gasError ? 'ok' : 'unconnected'}>
        <strong>Autorización:</strong> allowlist = planilla <code>Usuarios Portal</code> (módulo{' '}
        <code>analytics</code>, o rol admin / *). Fallback opcional:{' '}
        <code>ANALYTICS_ADMINS</code> en analytics-api.
        {data && data.system && data.system.gasError
          ? ' · Error al leer integraciones: ' + data.system.gasError
          : ''}
      </SourceBanner>

      <ErrorState message={error} />

      <div className="leads-subnav config-tabs" role="tablist" aria-label="Secciones de configuración">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={'leads-subnav-link' + (tab === t.id ? ' is-active' : '')}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'etapas' ? (
        <section className="analytics-section">
          <SectionHeader
            title="Administrar etapas"
            note="Estados internos (Abierto/Cerrado) + mapeo de cada etapa real de Kommo."
          />
          <StagesConfigPanel />
        </section>
      ) : null}

      {tab === 'origenes' ? (
        <section className="analytics-section">
          <SectionHeader
            title="Orígenes por etiqueta"
            note="Clasificá etiquetas Kommo como Meta (pagado) o No Meta (cada una es un origen aparte)."
          />
          <OriginTagsPanel />
        </section>
      ) : null}

      {loading && tab !== 'etapas' && tab !== 'origenes' ? (
        <p className="analytics-empty">Cargando…</p>
      ) : null}

      {!loading && data && tab === 'integraciones' ? (
        <section className="analytics-section">
          <SectionHeader title="Integraciones" note="Estado operativo — sin tokens ni claves." />
          <div className="analytics-module-grid">
            {data.integrations.map((it) => (
              <IntegrationStatusCard
                key={it.name}
                name={it.name}
                status={it.status}
                lastSync={it.lastSync}
                detail={it.detail}
              />
            ))}
          </div>
        </section>
      ) : null}

      {!loading && data && tab === 'fuentes' ? (
        <section className="analytics-section">
          <SectionHeader title="Fuentes de datos" />
          <SimpleTable
            columns={[
              { key: 'name', label: 'Nombre' },
              { key: 'type', label: 'Tipo' },
              { key: 'status', label: 'Estado' },
              { key: 'lastSync', label: 'Última sincronización' },
              { key: 'records', label: 'Registros' },
              { key: 'errors', label: 'Errores recientes' }
            ]}
            rows={data.sources}
          />
        </section>
      ) : null}

      {!loading && data && tab === 'usuarios' ? (
        <section className="analytics-section">
          <SectionHeader
            title="Usuarios"
            note="Interfaz preparada. Alta/baja y permisos se gestionan en backend."
          />
          <SimpleTable
            columns={[
              { key: 'email', label: 'Usuario' },
              { key: 'role', label: 'Rol' },
              { key: 'status', label: 'Estado' },
              { key: 'lastAccess', label: 'Último acceso' }
            ]}
            rows={data.users}
            emptyText="Sin datos de usuarios en este cliente. Listado vía ANALYTICS_ADMINS / admin portal (backend)."
          />
        </section>
      ) : null}

      {!loading && data && tab === 'roles' ? (
        <section className="analytics-section">
          <SectionHeader title="Roles previstos" />
          <ul className="analytics-role-list">
            {data.roles.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <p className="analytics-section-note">
            En desarrollo: matriz de permisos por módulo. No aplicar reglas solo en frontend.
          </p>
        </section>
      ) : null}

      {!loading && data && tab === 'sync' ? (
        <section className="analytics-section">
          <SectionHeader
            title="Caché de dashboards"
            note="Planilla _GICAPITAL - Analytics. Se precalculan períodos Este mes / 30 días / 7 días."
          />
          <div className="analytics-panel" style={{ marginBottom: '1.25rem' }}>
            <p>
              <strong>Planilla:</strong>{' '}
              {cache && cache.spreadsheetName
                ? cache.spreadsheetName
                : '—'}{' '}
              <code style={{ fontSize: '0.85em' }}>
                {(cache && cache.spreadsheetId) || '1WZEPHUCO9uf0HeyvMj7de-D0lx2jq6KLkcoBRrbdZ0c'}
              </code>
            </p>
            <p>
              <strong>Último refresh:</strong> {(cache && cache.lastRefreshAt) || '—'} ·{' '}
              <strong>Estado:</strong> {(cache && cache.lastRefreshStatus) || '—'} ·{' '}
              <strong>Por:</strong> {(cache && cache.lastRefreshBy) || '—'}
            </p>
            <p>
              <strong>Trigger 30 min:</strong>{' '}
              {cache && cache.trigger && cache.trigger.installed ? 'Instalado' : 'No instalado'}
            </p>
            {cache && cache.openError ? (
              <p className="analytics-section-note">
                Sin acceso a la planilla: {cache.openError}. Compartila con la cuenta del
                analytics-api y corré SETUP_authorizeOpsSheets en el editor GAS.
              </p>
            ) : null}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="analytics-btn"
                disabled={!!cacheBusy}
                onClick={onEnsureSheets_}
              >
                {cacheBusy === 'ensure' ? 'Creando hojas…' : 'Crear / verificar hojas'}
              </button>
              <button
                type="button"
                className="analytics-btn"
                disabled={!!cacheBusy}
                onClick={onInstallTrigger_}
              >
                {cacheBusy === 'trigger' ? 'Instalando…' : 'Instalar trigger 30 min'}
              </button>
              <button
                type="button"
                className="analytics-btn"
                disabled={!!cacheBusy}
                onClick={onRefreshCache_}
              >
                {cacheBusy === 'refresh' ? 'Actualizando…' : 'Actualizar caché ahora'}
              </button>
            </div>

            {cacheMsg ? <p className="analytics-section-note" style={{ marginTop: '0.75rem' }}>{cacheMsg}</p> : null}
            {cacheErr ? (
              <p className="analytics-section-note" style={{ marginTop: '0.5rem', color: '#b42318' }}>
                {cacheErr}
              </p>
            ) : null}
          </div>

          <SectionHeader title="Jobs" />
          <SimpleTable
            columns={[
              { key: 'name', label: 'Job' },
              { key: 'lastRun', label: 'Última ejecución' },
              { key: 'nextRun', label: 'Próxima' },
              { key: 'duration', label: 'Duración' },
              { key: 'records', label: 'Registros' },
              { key: 'status', label: 'Estado' },
              { key: 'errors', label: 'Errores' }
            ]}
            rows={data.syncJobs}
            emptyText="Sin jobs. Usá «Actualizar caché ahora» para el primer materializado."
          />

          {cache && cache.index && cache.index.length ? (
            <>
              <SectionHeader title="Índice de caché" note="Claves materializadas en Cache_Index." />
              <SimpleTable
                columns={[
                  { key: 'module', label: 'Módulo' },
                  { key: 'periodKey', label: 'Período' },
                  { key: 'dateFrom', label: 'Desde' },
                  { key: 'dateTo', label: 'Hasta' },
                  { key: 'generatedAt', label: 'Generado' },
                  { key: 'status', label: 'Estado' },
                  { key: 'bytes', label: 'Bytes' },
                  { key: 'durationMs', label: 'ms' },
                  { key: 'error', label: 'Error' }
                ]}
                rows={cache.index}
              />
            </>
          ) : null}
        </section>
      ) : null}

      {!loading && data && tab === 'logs' ? (
        <section className="analytics-section">
          <SectionHeader title="Logs" />
          <SimpleTable
            columns={[
              { key: 'date', label: 'Fecha' },
              { key: 'level', label: 'Nivel' },
              { key: 'module', label: 'Módulo' },
              { key: 'action', label: 'Acción' },
              { key: 'message', label: 'Mensaje' },
              { key: 'user', label: 'Usuario' }
            ]}
            rows={data.logs}
            emptyText="Sin datos. Auditoría Analytics_Audit se consulta vía GAS (pendiente de visor)."
          />
        </section>
      ) : null}

      {!loading && data && tab === 'sistema' ? (
        <section className="analytics-section">
          <SectionHeader title="Sistema" />
          <div className="analytics-panel analytics-coming-soon-panel">
            <p>
              <strong>Versión:</strong> {data.system.version}
            </p>
            <p>
              <strong>Entorno (host):</strong> {data.system.environment}
            </p>
            <p>
              <strong>Deploy:</strong> {data.system.deployHint}
            </p>
            <p>
              <strong>Módulos activos en nav:</strong> {data.system.modules.join(', ')}
            </p>
          </div>
        </section>
      ) : null}
    </div>
  );
}
